// src/pages/LatecomerAnalytics.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Treemap, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'react-toastify';
import API from '../../services/API';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList } from 'recharts';

/**
 * LatecomerAnalytics
 *
 * Fixes & improvements (why):
 * - Stable callbacks + deps to avoid stale closures and duplicate fetches.
 * - Merge/paginate safely; dedupe by student_id to prevent duplicates across pages.
 * - Robust Tooltip formatter (Recharts arg signature).
 * - Keys use student_id instead of array index to keep DOM stable.
 * - Loading/empty states for better UX.
 */

const COLORS = ['#ef4444', '#f87171', '#dc2626', '#b91c1c', '#fb923c', '#facc15'];
const now = new Date();
const CURRENT_MONTH = now.toISOString().slice(0, 7); // YYYY-MM
const EXCLUDED_DATE = "2026-01-29";

function shortenName(fullName = '') {
  if (!fullName) return '';
  const blacklist = ['MUHAMMAD', 'MOHAMAD', 'MOHD', 'AHMAD', 'NUR', "BIN", 'BINTI', 'NOR', 'WAN', 'SITI'];
  const parts = fullName
    .toUpperCase()
    .split(/\s+/)
    .filter((p) => p && !blacklist.includes(p));
  return parts.slice(0, 2).join(' ');
}
function LatecomerBarChart({ data }) {
  const chartData = useMemo(() => {
    return [...data]
      .sort((a, b) => b.count - a.count)
      .slice(0, 20) // top 15 latecomers
      .map((s) => ({
        name: shortenName(s.student_name),
        fullName: s.student_name,
        count: s.count,
        class_name: s.class_name,
        session: s.session,
      }));
  }, [data]);

  return (
    <div style={{ width: "100%", height: 420, padding: "10px" }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{ top: 10, right: 30, left: 50, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="name"
            width={160}
            tick={{
              fontSize: 15,
              fontWeight: "600",
              fill: "#222",
            }}
          />
          <Tooltip
            formatter={(value, name, props) => [
              `${value} times late`,
              `${props.payload.fullName} (${props.payload.class_name})`,
            ]}
          />

          <Bar dataKey="count" fill="#ef4444" radius={[6, 6, 6, 6]}>
            <LabelList
              dataKey="count"
              position="right"
              fill="#222"
              fontSize={13}
              fontWeight="600"
            />
          </Bar>


        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
function wrapText(text = '', maxChars = 10) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let line = '';
  for (const w of words) {
    const next = (line + ' ' + w).trim();
    if (next.length <= maxChars) line = next;
    else {
      if (line) lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function CustomContent(props) {
  const { x, y, width, height, name, size, index } = props;
  const fillColor = COLORS[index % COLORS.length];
  const maxChars = Math.max(6, Math.floor(width / 6));
  const wrapped = wrapText(name, maxChars);

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} style={{ fill: fillColor, stroke: '#fff', strokeWidth: 2, opacity: 0.9 }} />
      {width > 100 && height > 40 && (
        <text x={x + width / 2} y={y + height / 2 - wrapped.length * 6} textAnchor="middle" fill="#fff" fontSize={18} fontWeight="bold">
          {wrapped.map((line, i) => (
            <tspan key={i} x={x + width / 2} dy={i === 0 ? 0 : '1.2em'}>
              {line}
            </tspan>
          ))}
          <tspan x={x + width / 2} dy="1.5em" fontSize={14} fontWeight="normal">
            {size} times
          </tspan>
        </text>
      )}
    </g>
  );
}


export default function LatecomerAnalytics() {
  const [latecomers, setLatecomers] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(CURRENT_MONTH);
  const [sessionFilter, setSessionFilter] = useState("all");

  const [lateCutoff, setLateCutoff] = useState('08:00');
  const [availableSessions, setAvailableSessions] = useState(["Morning"]); // default

  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const [studentDetails, setStudentDetails] = useState({});
  const [loadingStudentId, setLoadingStudentId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const fetchStudentLateDetails = async (studentId) => {
    setLoadingStudentId(studentId);
    try {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        token: localStorage.getItem('token'),
      };
      const res = await API.get(`/analytics/attendance/latecomers/${studentId}`, {
        headers,
        params: { month: selectedMonth, is_portal: true },
      });

      if (res?.data?.response_code === 1000) {
        setStudentDetails((prev) => ({
          ...prev,
          [studentId]: res.data.data || [],
        }));
        setExpandedStudentId(studentId);
      } else {
        toast.error(res?.data?.message || 'Failed to fetch late records');
      }
    } catch (err) {
      toast.error(err?.message || 'Error fetching late records');
    } finally {
      setLoadingStudentId(null);
    }
  };

  const handleRowClick = (studentId) => {
    if (expandedStudentId === studentId) {
      setExpandedStudentId(null); // collapse
    } else if (studentDetails[studentId]) {
      setExpandedStudentId(studentId); // already fetched, just expand
    } else {
      fetchStudentLateDetails(studentId); // first time → fetch
    }
  };

  // Format 'HH:mm' -> 'h:mm AM/PM' (why: display friendly)
  const formatTo12h = (value) => {
    const [hStr = '', mStr = '00'] = String(value || '').split(':');
    const hNum = Number(hStr);
    const mNum = Number(mStr);
    if (!Number.isFinite(hNum) || hNum < 0 || hNum > 23) return value || '08:00';
    const suffix = hNum >= 12 ? 'PM' : 'AM';
    const hour12 = hNum % 12 === 0 ? 12 : hNum % 12;
    const mm = String(Number.isFinite(mNum) ? mNum : 0).padStart(2, '0');
    return `${hour12}:${mm} ${suffix}`;
  };

  const fetchLateConfig = useCallback(async () => {
    try {
      const headers = { 'Content-Type': 'application/json', Accept: 'application/json', token: localStorage.getItem('token') };
      const res = await API.get('/portal/attendance/lateConfig', { headers });
      if (res?.data?.response_code === 1000) {
        const v = res.data?.data?.late_checkin;
        if (typeof v === 'string' && v) setLateCutoff(v);
      }
    } catch (_) {
      // silent
    }
  }, []);
  const fetchLatecomers = useCallback(
    async (pageNum = 1, month = selectedMonth, session = sessionFilter, name = searchQuery) => {
      setLoading(true);
      try {
        const headers = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          token: localStorage.getItem('token')
        };
        const res = await API.get('/analytics/attendance/latecomers', {
          headers,
          params: {
            is_portal: true,
            month,
            page: pageNum,
            per_page: perPage,
            session: session !== "all" ? session : undefined,
            name: name || undefined,
          },
        });

        if (res?.data?.response_code === 1000) {
          const newData = Array.isArray(res.data.data) ? res.data.data : [];
          setAvailableSessions(res.data.available_sessions || ["Morning"]);
          const pagination = res.data.pagination || {};

          // 🔹 Reset list when it's a new search or page 1
          setLatecomers((prev) => {
            if (pageNum === 1) return newData; // reset on new search
            return [...prev, ...newData]; // append on load more
          });

          setPage(pageNum);
          setTotalPages(pagination.total_pages || 1);
          setTotalCount(pagination.total || newData.length);
        } else {
          toast.error(res?.data?.message || 'Failed to fetch latecomers', { autoClose: 5000 });
        }
      } catch (err) {
        toast.error(err?.message || 'Failed to fetch latecomers', { autoClose: 5000 });
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [perPage, selectedMonth, sessionFilter, searchQuery]
  );


  useEffect(() => {
    const handler = setTimeout(() => {
      setInitialLoading(true);
      fetchLatecomers(1, selectedMonth, sessionFilter, searchQuery);
    }, 400); // wait 400ms after typing stops
    return () => clearTimeout(handler);
  }, [selectedMonth, sessionFilter, searchQuery, fetchLatecomers]);


  useEffect(() => { fetchLateConfig(); }, [fetchLateConfig]);

  const latecomerSummary = useMemo(
    () =>
      (latecomers || []).map((l) => ({
        student_id: l.student_id,
        student_name: l.student_name,
        class_name: l.class_name || 'Unknown',
        session: l.session || 'Morning',
        profile_pic: l.profile_pic,
        count: Number(l.late_count) || 0,
        last_date: l.last_date,
        last_checkin: l.last_checkin,
      })),
    [latecomers]
  );
  const filteredLatecomers = useMemo(() => {
    let list = latecomerSummary;

    //  Remove latecomers ONLY for 29-01-2026
    list = list.filter((s) => {
      if (!s.last_date) return true;
      return s.last_date !== EXCLUDED_DATE;
    });

    // Session filter (unchanged)
    if (sessionFilter !== "all") {
      list = list.filter(
        (s) => (s.session || "").toLowerCase() === sessionFilter.toLowerCase()
      );
    }

    return list;
  }, [latecomerSummary, sessionFilter]);

  return (
    <div className="container-fluid mt-4">
      <div className="card rounded-25 shadow border-0">
        <div
          className="card-header text-white d-flex flex-wrap gap-3 justify-content-between align-items-center"
          style={{ backgroundColor: '#dc2626', borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}
        >
          <h5 className="mb-0 fw-bold">Latecomers Analytics (After {formatTo12h(lateCutoff)} MYT)</h5>
          {/* 🔹 Search bar */}
          <div className="ms-auto d-flex align-items-center gap-2">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search student..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ borderRadius: "20px", minWidth: "180px" }}
            />
          </div>
          <div className="d-flex gap-2 align-items-center">
            <select
              className="form-select form-select-sm"
              style={{ borderRadius: "20px", fontWeight: 500 }}
              value={sessionFilter}
              onChange={(e) => setSessionFilter(e.target.value)}
            >
              <option value="all">All Sessions</option>
              {availableSessions.map((s) => (
                <option key={s.toLowerCase()} value={s.toLowerCase()}>
                  {s}
                </option>
              ))}
            </select>

          </div>

          <div className="ms-auto">
            <input type="month" className="form-control form-control-sm w-auto" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
          </div>
        </div>

        {initialLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status" />
          </div>
        ) : (
          <LatecomerBarChart data={filteredLatecomers} />
        )}

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-danger">
                <tr>
                  <th style={{ minWidth: 220 }}>Student</th>
                  <th className="text-center">Class</th>
                  <th className="text-center">Times Late</th>
                  <th className="text-center">Last Late Date</th>
                  <th className="text-center">Last Check-In</th>
                  <th className="text-center">Session</th>
                </tr>
              </thead>
              <tbody>
                {filteredLatecomers.length > 0 ? (
                  filteredLatecomers.map((s) => (
                    <>
                      <tr
                        key={s.student_id}
                        style={{ cursor: "pointer" }}
                        onClick={() => fetchStudentLateDetails(s.student_id)}
                      >
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            <div>
                              <div className="fw-bold">{s.student_name || s.student_id}</div>
                              <div className="text-muted small">{s.student_id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-center">{s.class_name}</td>
                        <td className="text-center fw-bold text-danger">{s.count}</td>
                        <td className="text-center">{s.last_date || '-'}</td>
                        <td className="text-center text-danger fw-semibold">{s.last_checkin || '-'}</td>
                        <td className="text-center">{s.session}</td>


                      </tr>
                      {expandedStudentId === s.student_id && (
                        <tr>
                          <td colSpan={6} className="bg-white">
                            {loadingStudentId === s.student_id ? (
                              <div className="p-3 text-center text-muted">
                                <div className="spinner-border spinner-border-sm me-2" />
                                Loading records...
                              </div>
                            ) : studentDetails[s.student_id]?.length > 0 ? (
                              <table className="table table-sm mb-0">
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Check-in</th>
                                    <th>Session</th>
                                    <th>Class</th>
                                    <th>Remarks</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {studentDetails[s.student_id].map((r, idx) => (
                                    <tr key={idx}>
                                      <td>{r.date}</td>
                                      <td>{r.checkin_time}</td>
                                      <td>{r.session}</td>
                                      <td>{r.class_name}</td>
                                      <td>{r.remarks || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div className="p-3 text-center text-muted">No late records found.</div>
                            )}
                          </td>
                        </tr>
                      )}
                    </>

                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center text-muted p-4">
                      No latecomer records found for this month.
                    </td>
                  </tr>
                )}

              </tbody>
            </table>
          </div>

          {page < totalPages && (
            <div className="text-center p-3">
              <button
                className="btn px-4 py-2"
                style={{ borderRadius: '25px', backgroundColor: '#dc2626', color: 'white', fontWeight: 600 }}
                disabled={loading}
                onClick={() => fetchLatecomers(page + 1, selectedMonth, sessionFilter)}
              >
                {loading ? (
                  <span>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Loading...
                  </span>
                ) : (
                  'Load More'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
